import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Card,
  Spinner,
  Alert,
  Container,
  Badge,
  Dropdown,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import useReview from "hooks/reviews/useReview";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import Unauthorised from "components/access-control/rbac/Unauthorised";

const getName = (r) =>
  r?.preferredFirstName || r?.firstName || r?.nickName || "Unknown User";

const getInitials = (r = {}) => {
  const fn = r.preferredFirstName || r.firstName || "";
  const ln = r.preferredLastName || r.lastName || "";
  if (!fn && !ln) {
    const nick = r.nickName || "";
    const parts = nick.split(" ");
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  }
  return (fn[0] || "") + (ln[0] || "");
};

const StarRating = ({ rating }) => {
  if (!rating) return <small className="text-muted">No rating</small>;
  const stars = Array.from({ length: 5 }, (_, i) =>
    i < Math.floor(rating) ? "★" : "☆"
  );
  return <span style={{ color: "#ffc107" }}>{stars.join("")}</span>;
};

const statusMap = {
  ACTIVE: "success",
  DELETED: "danger",
  DEACTIVATED: "warning",
  HIDE: "secondary",
};

const ReviewCard = ({ review, onDelete, onStatusChange }) => {
  const name = getName(review.reviewer);
  const initials = getInitials(review.reviewer);
  const statusColor = statusMap[review.status] || "secondary";

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        {/* Reviewer info row */}
        <div className="d-flex flex-wrap align-items-center mb-2">
          {review.reviewer?.profilePhoto ? (
            <img
              src={review.reviewer.profilePhoto}
              alt={name}
              width={48}
              height={48}
              style={{
                borderRadius: "50%",
                objectFit: "cover",
                marginRight: 12,
                flexShrink: 0,
              }}
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#6c757d",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 12,
                fontWeight: "bold",
                fontSize: 18,
                userSelect: "none",
                flexShrink: 0,
              }}
            >
              {initials.toUpperCase()}
            </div>
          )}

          <div>
            <strong>{name}</strong>
            <br />
            <small className="text-muted">
              {new Date(review.createdAt).toLocaleDateString()}
            </small>
          </div>

          <Badge
            bg={statusColor}
            className="ms-auto mt-2 mt-sm-0 text-uppercase"
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              padding: "0.25em 0.5em",
            }}
          >
            {review.status}
          </Badge>
        </div>

        {/* Star rating */}
        <StarRating rating={review.rating} />

        {/* Review description */}
        <p className="mt-2 mb-0" style={{ whiteSpace: "pre-line" }}>
          {review.description || (
            <i className="text-muted">No comment provided</i>
          )}
        </p>
        {/* Action buttons below */}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-primary"
              size="sm"
              className="shadow-sm"
            >
              Status
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(statusMap).map((s) => (
                <Dropdown.Item
                  key={s}
                  active={review.status === s}
                  onClick={() => onStatusChange(review.id, s)}
                >
                  {s}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="outline-danger"
            size="sm"
            className="shadow-sm"
            onClick={() => onDelete(review.id)}
          >
            Delete
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const ProfileReviewsPage = () => {
  const { role, userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = location.state?.userName || "";

  const isRequester = role === "requester";

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const {
    reviews = [],
    loading,
    error,
    totalElements,
    updateReviewStatus,
    deleteReview,
  } = useReview(
    isRequester
      ? { requesterUserId: userId, size, page }
      : { helperUserId: userId, size, page }
  );

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    try {
      await deleteReview(confirmDeleteId, "SOFT");
      setConfirmDeleteId(null);
    } catch {
      setConfirmDeleteId(null);
    }
  };

  const handleChangeStatus = async (id, status) => {
    try {
      await updateReviewStatus(id, status);
    } catch {}
  };

  return (
    <Guard privilege={PRIV.VIEW_USERS} fallback={<Unauthorised />}>
      <Container>
        {/* Header */}
        <Row className="align-items-center mb-3">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark d-flex align-items-center gap-1"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              style={{ textDecoration: "none", fontWeight: "500" }}
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </Col>
          <Col xs className="text-center">
            <h3 style={{ fontWeight: 600, fontSize: 24, marginBottom: 0 }}>
              Profile Reviews
            </h3>
          </Col>
          <Col xs="auto" style={{ width: 32 }} /> {/* spacer */}
        </Row>

        {/* Loading/Error */}
        {loading && (
          <div className="d-flex align-items-center mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading reviews...
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error.message || "Failed to load reviews."}
          </Alert>
        )}

        {!loading && !error && reviews.length === 0 && (
          <p className="text-muted">No reviews yet.</p>
        )}

        {/* Review List */}
        {reviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            onDelete={handleDelete}
            onStatusChange={handleChangeStatus}
          />
        ))}

        {/* Pagination Controls */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 gap-2">
          <div>
            <Button
              variant="outline-primary"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="me-2"
            >
              &laquo; Previous
            </Button>
            <Button
              variant="outline-primary"
              disabled={(page + 1) * size >= totalElements}
              onClick={() => setPage(page + 1)}
            >
              Next &raquo;
            </Button>
          </div>

          <div className="d-flex align-items-center gap-2">
            <label htmlFor="pageSizeSelect" className="mb-0">
              Reviews per page:
            </label>
            <select
              id="pageSizeSelect"
              value={size}
              onChange={(e) => {
                setSize(Number(e.target.value));
                setPage(0);
              }}
              className="form-select form-select-sm"
              style={{ width: 80 }}
            >
              {[5, 10, 20].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Confirm Delete Modal */}
        <Modal
          show={!!confirmDeleteId}
          onHide={() => setConfirmDeleteId(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this review?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Guard>
  );
};

export default ProfileReviewsPage;
